import { Button, ButtonColors } from '@chiroup/components/Button';
import { Checkbox } from '@chiroup/components/form/Checkbox';
import {
  SupportAccessGrant,
  SupportAccessGrantStatus,
} from '@chiroup/core/types/SupportAccess.type';
import dayjs from 'dayjs';
import React, { useContext, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { ToastContext, ToastTypes } from '../../../contexts/toast.context';
import supportAccessService from '../../../services/supportAccess.service';

type Props = {
  clinicId: number;
};

const DURATION_OPTIONS: { label: string; ms: number }[] = [
  { label: '24 hours', ms: 24 * 60 * 60 * 1000 },
  { label: '3 days', ms: 3 * 24 * 60 * 60 * 1000 },
  { label: '1 week', ms: 7 * 24 * 60 * 60 * 1000 },
  { label: '2 weeks', ms: 14 * 24 * 60 * 60 * 1000 },
  { label: '4 weeks', ms: 28 * 24 * 60 * 60 * 1000 },
  { label: '8 weeks', ms: 56 * 24 * 60 * 60 * 1000 },
];

const STATUS_STYLES: Record<SupportAccessGrantStatus, string> = {
  active: 'bg-green-100 text-green-800',
  pending: 'bg-amber-100 text-amber-800',
  denied: 'bg-red-100 text-red-800',
  revoked: 'bg-gray-100 text-gray-700',
  expired: 'bg-gray-100 text-gray-500',
};

// The backend bakes the clinic-app host into launchUrl from its own deployed
// stage, so it can't know when the admin app is running locally. Re-point the
// launch URL at the host this frontend is configured for (VITE_CHIROUP_URL),
// preserving the path and token hash. Falls back to the backend URL when unset.
const resolveLaunchUrl = (launchUrl: string): string => {
  const overrideBase = import.meta.env.VITE_CHIROUP_URL as string | undefined;
  if (!overrideBase) return launchUrl;
  try {
    const target = new URL(launchUrl);
    const base = new URL(overrideBase);
    target.protocol = base.protocol;
    target.host = base.host;
    return target.toString();
  } catch {
    return launchUrl;
  }
};

const ClinicSupportAccess: React.FC<Props> = ({ clinicId }) => {
  const { createToast } = useContext(ToastContext);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [durationMs, setDurationMs] = useState(DURATION_OPTIONS[2].ms);
  const [reason, setReason] = useState('');
  const [excludeExpired, setExcludeExpired] = useState(true);

  const { data, isFetching, refetch } = useQuery(
    ['supportAccessGrants', clinicId, excludeExpired],
    () => supportAccessService.listGrants(clinicId, excludeExpired),
    { enabled: !!clinicId, refetchOnWindowFocus: false }
  );

  const startSession = useMutation(
    (grantId: string) => supportAccessService.startSession(clinicId, grantId),
    {
      onSuccess: res => {
        window.open(resolveLaunchUrl(res.launchUrl), '_blank', 'noopener,noreferrer');
        refetch();
      },
      onError: (err: any) => {
        createToast({
          title: 'Support access failed',
          type: ToastTypes.Fail,
          description: err?.response?.data?.message || 'Unable to start a support access session.',
          duration: 5000,
        });
      },
    }
  );

  const requestAccess = useMutation(
    () =>
      supportAccessService.requestAccess(clinicId, {
        expiresAt: Date.now() + durationMs,
        reason: reason.trim() || undefined,
      }),
    {
      onSuccess: () => {
        createToast({
          title: 'Access requested',
          type: ToastTypes.Success,
          description: 'The clinic has been asked to grant support access.',
          duration: 5000,
        });
        setShowRequestForm(false);
        setReason('');
        refetch();
      },
      onError: () => {
        createToast({
          title: 'Request failed',
          type: ToastTypes.Fail,
          description: 'Unable to request support access.',
          duration: 5000,
        });
      },
    }
  );

  const grants = data?.grants || [];

  return (
    <div className="m-6 rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Support Access</h2>
          <p className="mt-1 text-sm text-gray-500">
            Request access from the clinic, or start a time-boxed session from an active grant.
          </p>
        </div>
        <Button
          text={showRequestForm ? 'Cancel' : 'Request access'}
          color={showRequestForm ? ButtonColors.plainWithBorder : ButtonColors.primary}
          onClick={() => setShowRequestForm(prev => !prev)}
        />
      </div>

      {showRequestForm && (
        <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
          <p className="text-sm text-gray-600">
            The clinic will see this request on their Support Hub and can grant or deny it. Access
            stays blocked until they grant it, and support acts as the user who approves.
          </p>
          <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-end">
            <label className="flex flex-col text-sm text-gray-700">
              <span className="mb-1 font-medium">Requested duration</span>
              <select
                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                value={durationMs}
                onChange={e => setDurationMs(Number(e.target.value))}
              >
                {DURATION_OPTIONS.map(option => (
                  <option key={option.ms} value={option.ms}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-1 flex-col text-sm text-gray-700">
              <span className="mb-1 font-medium">Reason (optional)</span>
              <input
                type="text"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="e.g. Investigating a billing discrepancy"
                value={reason}
                maxLength={500}
                onChange={e => setReason(e.target.value)}
              />
            </label>
            <Button
              text="Send request"
              color={ButtonColors.primary}
              loading={requestAccess.isLoading}
              disabled={requestAccess.isLoading}
              onClick={() => requestAccess.mutate()}
            />
          </div>
        </div>
      )}

      <div className="p-6">
        <div className="mb-4 flex items-center gap-3">
          <Checkbox
            name="excludeExpired"
            label="Exclude expired sessions."
            value={excludeExpired}
            onChange={setExcludeExpired}
          />
          {excludeExpired && !!data?.inactiveCount && (
            <span className="text-sm text-gray-500">{data.inactiveCount} hidden</span>
          )}
        </div>
        {isFetching ? (
          <div className="text-sm text-gray-500">Loading support access grants...</div>
        ) : !grants.length ? (
          <div className="text-sm text-gray-500">No support access grants found.</div>
        ) : (
          <div className="overflow-hidden rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                    Source
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                    Reason
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                    Expires
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-semibold uppercase text-gray-500">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {grants.map((grant: SupportAccessGrant) => {
                  const isActive = grant.status === 'active' && grant.expiresAt > Date.now();
                  const isAdminRequest = grant.source === 'admin_request';

                  return (
                    <tr key={grant.id}>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {isAdminRequest ? (
                          <>
                            <div>Support access request</div>
                            {grant.actAsUserName ? (
                              <div className="text-xs text-gray-500">
                                Acts as {grant.actAsUserName}
                              </div>
                            ) : grant.requestedByAdminEmail ? (
                              <div className="text-xs text-gray-500">
                                Requested by {grant.requestedByAdminEmail}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <>
                            <div>
                              Acts as {grant.actAsUserName || grant.actAsUserId || 'a user'}
                            </div>
                            {grant.caseNumber && (
                              <div className="text-xs text-gray-500">Case {grant.caseNumber}</div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 max-w-xs">
                        {grant.reason ? (
                          <span className="italic" title={grant.reason}>
                            &ldquo;{grant.reason}&rdquo;
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
                            STATUS_STYLES[grant.status] || 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {grant.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {grant.status === 'pending' || grant.status === 'denied'
                          ? '—'
                          : dayjs(grant.expiresAt).format('MMM D, YYYY h:mm A')}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          text="Start session"
                          color={ButtonColors.primary}
                          disabled={!isActive || startSession.isLoading}
                          onClick={() => startSession.mutate(grant.id)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClinicSupportAccess;
